<template>
    <transition name="fade" mode="out-in">
        <div
            v-if="isActive"
            class="pop pop__side"
        >
            <div class="pop__side_overlay"></div>
            <div class="pop__side_box">
                <header class="pop__side_box_header">
                    <div
                        v-if="title"
                        class="pop__side_box_header_title"
                    >{{ title }}
                    </div>
                    <div class="pop__side_box_header_close">
                        <component-button
                            :name="'pop-side-close'"
                            :icon="true"
                            :theme="''"
                            :icon-name="'x'"
                            :icon-raw="true"
                            @click="handleClosePop"
                        ></component-button>
                    </div>
                </header>
                <div class="pop__side_box_main">
                    <slot></slot>
                </div>
                <footer class="pop__side_box_footer">
                    <div class="pop__side_box_footer_actions">
                        <div class="columns is-variable">
                            <div class="column"></div>
                            <template v-if="cancelButtonActive">
                                <div class="column is-narrow">
                                    <component-button
                                        :theme="'default'"
                                        :border="true"
                                        :name="'pop-side-box-footer-actions-close'"
                                        @click="handleClosePop"
                                    >{{cancelButtonText}}
                                    </component-button>
                                </div>
                            </template>
                            <template v-if="customButtonActive && !disabled">
                                <div class="column is-narrow">
                                    <component-button
                                        :theme="'default'"
                                        :border="true"
                                        :name="'pop-side-box-footer-actions-custom'"
                                        :disabled="customButtonDisabled"
                                        @click="performCustom"
                                    >{{customButtonText}}
                                    </component-button>
                                </div>
                            </template>
                            <template v-if="secondaryButtonActive && !disabled">
                                <div class="column is-narrow">
                                    <component-button
                                        :name="'pop-side-box-footer-actions-secondary'"
                                        :theme="secondaryButtonTheme"
                                        :icon="secondaryButtonIcon"
                                        :icon-name="secondaryButtonIconName"
                                        :icon-align="'right'"
                                        @click="emitSecondary"
                                    >{{secondaryButtonText}}
                                    </component-button>
                                </div>
                            </template>
                            <template v-if="primaryButtonActive && !disabled">
                                <div class="column is-narrow">
                                    <component-button
                                        :name="'pop-side-box-footer-actions-primary'"
                                        :theme="primaryButtonTheme"
                                        :icon="primaryButtonIcon"
                                        :icon-name="primaryButtonIconName"
                                        :icon-align="'right'"
                                        :disabled="primaryButtonDisabled"
                                        :animate-loading="primaryButtonAnimateLoading && !primaryButtonNotifyActive"
                                        :animate-loading-event-name="primaryButtonAnimateLoadingName"
                                        v-on="primaryButtonNotifyActive ? { 'click': openNotifyPrimary } : { 'click': emitPrimary }"
                                    >{{primaryButtonText}}
                                    </component-button>
                                </div>
                            </template>
                            <template v-if="menuButtonActive && menuButtonItems">
                                <div class="column is-narrow">
                                    <component-button-menu
                                        :name="'pop-side-box-footer-actions-menu'"
                                        :buttons="menuButtonItems"
                                        @emit-restore="$emit('emit-restore')"
                                    ></component-button-menu>
                                </div>
                            </template>
                        </div>
                    </div>
                </footer>
            </div>
            <pop-notify
                ref="popnotifyredirect"
                :class="'pop__notify--redirect'"
                @emit-submit="performCustomRedirect"
            />
            <pop-notify
                v-if="primaryButtonNotifyActive"
                ref="popnotifyprimarybutton"
                :prevent-close="primaryButtonAnimateLoading"
                :title-text="primaryButtonNotifyTitle"
                :content-text="primaryButtonNotifyContent"
                :cancel-button-text="'Cancel'"
                :submit-button-text="'Save'"
                :submit-button-animate-loading="primaryButtonAnimateLoading"
                :submit-button-animate-loading-name="primaryButtonAnimateLoadingName"
                @emit-submit="emitPrimary"
            ></pop-notify>
            <pop-notify
                ref="popnotifyclosepop"
                :title-text="'Discard changes?'"
                :content-text="'Any unsaved changes will not be saved.'"
                :cancel-button-text="'Cancel'"
                :submit-button-text="'Discard'"
                @emit-submit="closePop"
            ></pop-notify>
        </div>
    </transition>
</template>

<script>
import ComponentButton from '@/components/default/shared/ComponentButton'
import PopNotify from '@/components/default/shared/PopNotify'
import { getDomPath } from '@/functions'
import ComponentButtonMenu from '@/components/default/shared/ComponentButtonMenu'

export default {
  components: {
    ComponentButtonMenu,
    PopNotify,
    ComponentButton
  },
  name: 'pop-side',
  data () {
    return {
      isActive: false,
      preventClose: true,
      slotContentChanged: false
    }
  },
  props: {
    title: {
      type: String,
      required: true
    },
    cancelButtonActive: {
      type: Boolean,
      default: true
    },
    cancelButtonText: {
      type: String,
      default: function () {
        return 'Cancel'
      }
    },
    //
    customButtonActive: {
      type: Boolean,
      default: false
    },
    customButtonTo: {
      type: Object
    },
    customButtonText: {
      type: String,
      default: function () {
        return this.$t('shared.pop.buttons.reset')
      }
    },
    customButtonDisabled: {
      type: Boolean,
      default: false
    },
    //
    primaryButtonActive: {
      type: Boolean,
      default: true
    },
    primaryButtonText: {
      type: String,
      default: function () {
        return this.$t('shared.pop.buttons.update')
      }
    },
    primaryButtonIcon: {
      type: Boolean,
      default: false
    },
    primaryButtonIconName: {
      type: String
    },
    primaryButtonTheme: {
      type: String
    },
    primaryButtonDisabled: {
      type: Boolean,
      default: false
    },
    primaryButtonAnimateLoading: {
      type: Boolean,
      default: false
    },
    primaryButtonAnimateLoadingName: {
      type: String,
      default: 'submit'
    },
    primaryButtonNotifyActive: {
      type: Boolean,
      default: false
    },
    primaryButtonNotifyTitle: {
      type: String,
      default: 'Save changes?'
    },
    primaryButtonNotifyContent: {
      type: String,
      default: 'Any changes made will be applied to this notification where it is in use in the workspace.'
    },
    secondaryButtonActive: {
      type: Boolean,
      default: false
    },
    secondaryButtonText: {
      type: String,
      default: function () {
        return this.$t('shared.pop.buttons.custom')
      }
    },
    secondaryButtonIcon: {
      type: Boolean,
      default: false
    },
    secondaryButtonIconName: {
      type: String
    },
    secondaryButtonTheme: {
      type: String
    },
    menuButtonActive: {
      type: Boolean,
      default: false
    },
    menuButtonItems: {
      type: Array,
      default: () => []
    },
    //
    disabled: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    performEmit (message, value) {
      this.$emit('emit', { message: message, value: value })
    },
    performCustomRedirect () {
      this.$router.push(this.customButtonTo)
    },
    openPopCustomRedirect () {
      this.$refs.popnotifyredirect.openNotify()
    },
    openPop () {
      this.isActive = true
    },
    handleClosePop () {
      if (this.slotContentChanged) {
        this.openNotifyClosePop()
      } else {
        this.closePop()
      }
    },
    closePop () {
      this.isActive = false
      this.preventClose = true
      this.$emit('emit-close')
    },
    autoClosePop (event) {
      const path = event.path || event.composedPath()

      if (!path || !this.isActive) return
      if (this.preventClose) {
        this.preventClose = false
        return
      }
      let close = true
      for (let i = 0; i < path.length; i++) {
        if (!path[i].className) continue
        if (
          path[i].classList.contains('pop__side_box') ||
        path[i].classList.contains('pop__notify')
        ) {
          close = false
        }
      }
      if (close) {
        this.handleClosePop()
      }
    },
    //
    performCustom () {
      if (this.customButtonTo) {
        this.openPopCustomRedirect()
      } else {
        this.emitCustom()
      }
    },
    emitCustom () {
      this.preventClose = true
      this.$emit('emit-custom')
    },
    emitPrimary () {
      this.preventClose = true
      this.$emit('emit-primary')
    },
    emitSecondary () {
      this.preventClose = true
      this.$emit('emit-secondary')
    },
    openNotifyPrimary () {
      this.$refs.popnotifyprimarybutton.openNotify()
    },
    closeNotifyPrimary () {
      this.$refs.popnotifyprimarybutton.closeNotify()
    },
    openNotifyClosePop () {
      this.$refs.popnotifyclosepop.openNotify()
    },
    closeNotifyClosePop () {
      this.$refs.popnotifyclosepop.closeNotify()
    },
    handleSlotContentChanged () {
      this.slotContentChanged = true
    }
  },
  mounted () {
    const overlay = document.getElementById('app')
    overlay.addEventListener('click', this.autoClosePop)

    const domItem = this.$el
    const domPath = getDomPath(domItem)

    for (let i in domPath) {
      const domLoopItem = domPath[i]
      if (domLoopItem.classList && domLoopItem.classList.contains('pop')) {
        domLoopItem.append(domItem)
        break
      }
    }
  },
  beforeDestroy () {
    const overlay = document.getElementById('app')
    overlay.removeEventListener('click', this.autoClosePop)
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/shared/popside";
</style>
